import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCheck } from 'lucide-react';
import type { Notification, NotificationType } from '@pms/shared';
import { notificationsService } from '@/services/notifications.service';
import { DataTable, type Column } from '@/components/ui/data-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatRelativeTime } from '@/lib/dateFormat';

type BadgeVariant = 'default' | 'secondary' | 'warning' | 'success';

interface TypeMeta {
  label: string;
  variant: BadgeVariant;
}

const TYPE_META: Record<NotificationType, TypeMeta> = {
  sop_assigned:               { label: 'SOP',          variant: 'default'   },
  sop_due_soon:               { label: 'SOP',          variant: 'default'   },
  sop_overdue:                { label: 'SOP',          variant: 'default'   },
  notice_published:           { label: 'Notice',       variant: 'secondary' },
  ticket_assigned:            { label: 'Ticket',       variant: 'warning'   },
  ticket_status_changed:      { label: 'Ticket',       variant: 'warning'   },
  ticket_comment_added:       { label: 'Ticket',       variant: 'warning'   },
  ticket_escalated:           { label: 'Ticket',       variant: 'warning'   },
  subscription_expiring_soon: { label: 'Subscription', variant: 'success'   },
  subscription_expired:       { label: 'Subscription', variant: 'success'   },
  subscription_renewed:       { label: 'Subscription', variant: 'success'   },
  subscription_cancelled:     { label: 'Subscription', variant: 'success'   },
  task_no_assignee_available: { label: 'Task',         variant: 'warning'   },
  task_reminder:              { label: 'Task',         variant: 'warning'   },
  // Retired grace flow — kept so notifications sent before its removal still render.
  task_grace_requested:       { label: 'Task',         variant: 'warning'   },
  task_grace_reviewed:        { label: 'Task',         variant: 'warning'   },
  goal_assigned:              { label: 'Goal',         variant: 'default'   },
  goal_deadline_soon:         { label: 'Goal',         variant: 'warning'   },
  goal_overdue:               { label: 'Goal',         variant: 'warning'   },
  goal_progress_logged:       { label: 'Goal',         variant: 'default'   },
  goal_submitted:             { label: 'Goal',         variant: 'default'   },
  goal_reviewed:              { label: 'Goal',         variant: 'success'   },
};

const LIMIT = 20;

export function NotificationsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [unreadOnly, setUnreadOnly] = useState(false);

  const query = useQuery({
    queryKey: ['notifications', { page, limit: LIMIT, unread_only: unreadOnly }],
    queryFn: () => notificationsService.list({ page, limit: LIMIT, unread_only: unreadOnly }),
  });

  const unreadCountQuery = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => notificationsService.unreadSummary(),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['notifications'] });

  const markRead = useMutation({
    mutationFn: (id: string) => notificationsService.markRead(id),
    onSuccess: invalidate,
  });

  const markAll = useMutation({
    mutationFn: () => notificationsService.markAllRead(),
    onSuccess: invalidate,
  });

  const handleOpen = (n: Notification) => {
    if (!n.is_read) markRead.mutate(n.id);
    if (n.link) navigate(n.link);
  };

  const items = query.data?.data ?? [];
  const pagination = query.data?.pagination;
  const unreadCount = unreadCountQuery.data?.count ?? 0;

  const columns: Column<Notification>[] = [
    {
      key: 'type',
      header: 'Type',
      className: 'w-32',
      cell: (n) => {
        const meta = TYPE_META[n.type];
        return <Badge variant={meta.variant}>{meta.label}</Badge>;
      },
    },
    {
      key: 'title',
      header: 'Title',
      cell: (n) => (
        <button
          type="button"
          onClick={() => handleOpen(n)}
          className="flex items-center gap-2 text-left hover:underline"
        >
          {!n.is_read && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />}
          <span className="font-medium">{n.title}</span>
        </button>
      ),
    },
    {
      key: 'body',
      header: 'Details',
      cell: (n) => (
        <span className="max-w-xs truncate text-muted-foreground">{n.body ?? '—'}</span>
      ),
    },
    {
      key: 'time',
      header: 'Time',
      className: 'w-36 whitespace-nowrap',
      cell: (n) => (
        <span className="text-xs text-muted-foreground">{formatRelativeTime(n.created_at)}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-28 text-right',
      cell: (n) =>
        !n.is_read ? (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs"
            disabled={markRead.isPending}
            onClick={() => markRead.mutate(n.id)}
          >
            Mark read
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Notifications</h1>
          <p className="text-sm text-muted-foreground">
            SOP assignments, ticket updates, notices, and subscription alerts
          </p>
        </div>
        {unreadCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            disabled={markAll.isPending}
            onClick={() => markAll.mutate()}
            className="gap-1.5"
          >
            <CheckCheck className="h-4 w-4" />
            Mark all read
          </Button>
        )}
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => { setUnreadOnly(false); setPage(1); }}
          className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
            !unreadOnly
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:text-foreground'
          }`}
        >
          All
        </button>
        <button
          type="button"
          onClick={() => { setUnreadOnly(true); setPage(1); }}
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium transition-colors ${
            unreadOnly
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:text-foreground'
          }`}
        >
          Unread only
          {unreadCount > 0 && (
            <span className="rounded-full bg-rose-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
              {unreadCount}
            </span>
          )}
        </button>
      </div>

      <DataTable
        columns={columns}
        rows={items}
        rowKey={(n) => n.id}
        loading={query.isLoading}
        loadingRows={8}
        error={query.isError}
        onRetry={() => query.refetch()}
        empty={
          <div className="flex flex-col items-center gap-2 py-10 text-center">
            <Bell className="h-8 w-8 opacity-30" />
            <p className="text-sm text-muted-foreground">
              {unreadOnly ? 'No unread notifications' : "You're all caught up"}
            </p>
          </div>
        }
        pagination={
          pagination
            ? {
                page: pagination.page,
                totalPages: pagination.totalPages,
                total: pagination.total,
                onPageChange: setPage,
              }
            : undefined
        }
      />
    </div>
  );
}
